/**
 * ResearchButton - Button to trigger research creation on a contact or company.
 */

import { SearchLg } from '@untitledui/icons';
import { formatDistanceToNow } from 'date-fns';

import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { useCreateResearch } from './useCreateResearch';
import type { Research } from './useResearch';

/** Free email providers that don't identify a company */
const FREE_EMAIL_PROVIDERS = new Set([
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'protonmail.com',
  'mail.com',
]);

/**
 * Check if a contact or company has sufficient data for research.
 */
// eslint-disable-next-line react-refresh/only-export-components -- utility function co-located with ResearchButton component
export function canResearch(
  object: CrmObject,
  recordType: 'contact' | 'company'
): boolean {
  if (recordType === 'contact') {
    // Contact is researchable if:
    // 1. Has a name (first_name or last_name), OR
    // 2. Has a business email (not free provider), OR
    // 3. Has an association to a company
    const firstName = getPropertyValue<string>(
      object.properties?.ahoy_first_name
    );
    const lastName = getPropertyValue<string>(
      object.properties?.ahoy_last_name
    );
    const hasName = Boolean(firstName?.trim() || lastName?.trim());

    const emailValue = getPropertyValue<string | string[]>(
      object.properties?.ahoy_email
    );
    const email = Array.isArray(emailValue) ? emailValue[0] : emailValue;
    const emailDomain = email?.split('@')[1]?.toLowerCase();
    const hasBusinessEmail =
      emailDomain && !FREE_EMAIL_PROVIDERS.has(emailDomain);

    const hasCompanyAssociation = object.associations?.some(
      a => a.association_type_name === 'ahoy_contact_to_company'
    );

    return hasName || hasBusinessEmail || Boolean(hasCompanyAssociation);
  } else {
    // Company is researchable if:
    // 1. Has a name, OR
    // 2. Has a domain
    const name = getPropertyValue<string>(object.properties?.ahoy_name);
    const domain = getPropertyValue<string>(object.properties?.ahoy_domain);

    return Boolean(name?.trim() || domain?.trim());
  }
}

interface ResearchButtonProps {
  /** Organization ID */
  orgId: number;
  /** Entity ID of the contact or company */
  entityId: string;
  /** Record type: 'contact' or 'company' */
  recordType: 'contact' | 'company';
  /** The CRM object (contact or company) to check for research eligibility */
  object: CrmObject;
  /** Latest research for this record (optional, used to show "Last researched X ago") */
  latestResearch?: Research | null;
  /** Callback when research is created */
  onResearchCreated?: (researchId: string) => void;
}

/**
 * Research trigger button with context about last research.
 *
 * @example
 * <ResearchButton
 *   orgId={1}
 *   entityId="abc-123"
 *   recordType="contact"
 *   latestResearch={research[0]}
 * />
 */
export function ResearchButton({
  orgId,
  entityId,
  recordType,
  object,
  latestResearch,
  onResearchCreated,
}: ResearchButtonProps) {
  const { createResearch, isCreating } = useCreateResearch({
    orgId,
    entityId,
    recordType,
  });
  // Research creates an ahoy_research record — a write. Hooks stay above the
  // early return below; a seat without the grant renders no CTA at all.
  const { hasPermission } = usePermissions();
  const canGenerateResearch = hasPermission(
    'ahoy_research',
    'ahoy_object_created'
  );

  const handleClick = async () => {
    const researchId = await createResearch();
    if (researchId && onResearchCreated) {
      onResearchCreated(researchId);
    }
  };

  // Check if research is possible
  const isResearchable = canResearch(object, recordType);
  let disabledReason: string | undefined;
  if (!isResearchable) {
    if (recordType === 'contact') {
      disabledReason =
        'Add a name, business email, or company to enable research';
    } else {
      disabledReason = 'Add a company name or domain to enable research';
    }
  } else {
    disabledReason = undefined;
  }

  // Format last researched time
  const lastResearchedText = latestResearch?.created_at
    ? `Last researched ${formatDistanceToNow(new Date(latestResearch.created_at), { addSuffix: true })}`
    : null;

  const helpTextId = `research-help-${entityId}`;

  if (!canGenerateResearch) {
    return null;
  }

  return (
    <div className='flex flex-col items-start gap-1'>
      <Button
        color='secondary'
        size='sm'
        iconLeading={SearchLg}
        isLoading={isCreating}
        isDisabled={isCreating || !isResearchable}
        onClick={handleClick}
        aria-describedby={
          disabledReason || lastResearchedText ? helpTextId : undefined
        }
      >
        Research
      </Button>
      {disabledReason && (
        <span id={helpTextId} className='text-xs text-tertiary max-w-48'>
          {disabledReason}
        </span>
      )}
      {!disabledReason && lastResearchedText && (
        <span
          id={helpTextId}
          className='text-xs text-tertiary max-w-48 truncate'
          title={lastResearchedText}
        >
          {lastResearchedText}
        </span>
      )}
    </div>
  );
}
